import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { friendlyMetricName, renderValue } from '@upright/common/model/input/pai-assessments';
import { ApiPaiValue } from '@upright/common/model/output/eu-regulation/resolved-pais/types';
import { friendlyShortImpactTitle } from '@upright/common/utils';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { GenericDynamicHeightInfoBox } from '@upright/ui/common/upright/core/impact-lens-profile/common/components/info-boxes/generic-dynamic-height-info-box';
import { InfoBoxTitleRow } from '@upright/ui/common/upright/core/impact-lens-profile/common/components/info-boxes/info-box-title-row';
import { commonInfoBoxPadding } from '@upright/ui/common/upright/core/impact-lens-profile/common/components/info-boxes/styles';
import { Bounds, Side } from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/types';
import { getPaisForImpactCategory } from '@upright/ui/common/upright/core/impact-lenses/pais/common/pai-mapping';
import { usePaiLensContext } from '@upright/ui/common/upright/core/impact-lenses/pais/common/use-pai-lens-context';
import { compact } from 'lodash';
import { CSSProperties } from 'react';
import styled from 'styled-components';
import { useCarouselLayout } from '../../../impact-lens-profile/context';
import { getIconAndColorByNature } from '../common/icons';

interface Props {
  impact: ImpactCategoryId;
}

export function PaiInfoBox(props: Props) {
  const layout = useCarouselLayout();
  const boxesArea = layout.boxesArea(Side.LEFT);
  const offsetToFitPaiBoxContent = 16;
  const paiBoxArea: Bounds = { ...boxesArea, y1: boxesArea.y1 - offsetToFitPaiBoxContent };
  const ctx = usePaiLensContext();
  const pais = ctx?.data.pais;
  if (!pais) {
    return <></>;
  }
  const allIndicators = getPaisForImpactCategory(props.impact);
  const indicatorValues = compact(
    allIndicators.map(id => {
      return pais[id];
    }),
  );

  const elems = indicatorValues.map((value, index) => {
    return <IndicatorBlock key={index} data={value} />;
  });

  return (
    <GenericDynamicHeightInfoBox side={Side.LEFT} refArea={paiBoxArea} topColor={BouColor.IMPACT_NEGATIVE_PURPLE}>
      <InfoBoxTitleRow>
        <div>
          PAI indicator(s) related to <span>{friendlyShortImpactTitle(props.impact)}</span>
        </div>
      </InfoBoxTitleRow>
      <Main>{elems}</Main>
    </GenericDynamicHeightInfoBox>
  );
}

const Main = styled.div`
  ${commonInfoBoxPadding};
  padding-top: 0;
  font-size: 11px;
`;

interface IndicatorBlockProps {
  data: ApiPaiValue;
  style?: CSSProperties;
}

function IndicatorBlock(props: IndicatorBlockProps) {
  const { data } = props;
  const value = renderValue(data);
  const { Icon, color } = getIconAndColorByNature(data.nature);
  return (
    <IndicatorValueRoot>
      <TitleRow>
        <Title>{friendlyMetricName(data.metric)}</Title>
      </TitleRow>
      <FigureRow>
        <Figure>{value}</Figure>
        <Icon style={{ color, height: '1.0em', width: '1.0em' }} />
      </FigureRow>
    </IndicatorValueRoot>
  );
}

const IndicatorValueRoot = styled.div`
  padding-top: 0.7em;
  padding-bottom: 0.7em;
  & + & {
    border-top: 1px solid ${BouColor.TOOL_BORDER_LIGHTER_GRAY};
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.4em;
`;

const Title = styled.div`
  color: ${BouColor.TOOL_TEXT_DARK_GRAY};
  overflow: hidden;
`;

const Figure = styled.div`
  /*color: ${BouColor.TOOL_TEXT_GRAY};*/
`;

const FigureRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
